import { takeLatest, put, select } from 'redux-saga/effects';
import { replace } from 'connected-react-router';
import { cloneDeep, get } from 'lodash';
import update from 'immutability-helper';
import { message } from 'antd';
import { allDimensionsListSelector } from '@store/common/dimensionList/selectors';
import { getFirstAppliedFilter } from '@store/common/selectors';
import { ALL_TRAFFIC, createFilterRule } from '@utils/filter';
import { pushDrilldownFilter } from '@store/dimensionContainer/actions';
import { getFilterFromRules, getRulesFromFilter } from '@utils';
import { applyFilter } from '@store/common/actions';
import { DASHBOARD_TYPE, NOC_MODE_FILTERS, NOC_MODE_TRENDS, isAppModule } from '@utils/constants';
import { findRuleIndex, getRuleId } from '@utils/filter/ruleUtils';
import { compareConfigSelector } from '@store/comparison/selectors';
import { triggerChangeTabs } from '@store/dashboards/tabs/actions';
import addScrollingWindow from '@utils/addScrollingWindow';
import { MODIFY_COMPARISON } from '@store/comparison/types';
import { addFiltersFromNocDrawer } from '@store/noc/drawer/actions';
import shortId from '@utils/shortId';
import { COMPARE_SERIES_LIMIT } from '@components/metrics-panel-hz/utils/compareSeriesOption';
import { COMPARE_TYPES, ViewContexts, defaultCompareState } from './reducer';
import * as actions from './actions';
import * as types from './types';
import {
  activeFilterIndexSelector,
  activeFilterSelector,
  buildFilterSelector,
  compareFiltersSelector,
  compareOnTrendsFiltersSelector,
  compareSelector,
  compareTypeSelector,
  quickCompareRuleSetSelector,
  rulesSelector,
  viewContextSelector,
} from './selectors';
import { DIMENSION_TAG } from '@src/utils/dimension/createGroupBy';
import { ERROR_DIMENSION_MAP } from '@src/utils/dimension/errorCodeHelper';

const { baseUrl } = window.conviva;

export const getCompareLimitWarning = (limit) => `Reached max limit of ${limit} filters`;

function* initRule() {
  const appliedFilter = yield select(getFirstAppliedFilter);
  const rules = getRulesFromFilter(appliedFilter.rule, true);

  yield put(actions.setRules(Object.values(rules)));
}

function* buildRule(dimension, row) {
  if (dimension in ERROR_DIMENSION_MAP) {
    return buildErrorRule(dimension, row);
  }
  const dimensionList = yield select(allDimensionsListSelector);
  const dimensionObj = dimensionList.find((d) => d.groupBy === dimension);
  const rule = createFilterRule(
    dimensionList,
    dimensionObj.name,
    get(row, 'name[0]', ''),
    get(row, 'value[0]', ''),
  );
  return rule;
}

function buildErrorRule(dimension, row) {
  const display = get(row, 'name[0]', '');
  const value = get(row, 'value[0]', '');
  return {
    field: DIMENSION_TAG,
    key: dimension,
    op: 'eq',
    value,
    display,
  };
}

function* toggleRule(action) {
  const { dimension, row } = action;
  const rules = yield select(rulesSelector);
  const rule = yield buildRule(dimension, row);

  const ruleIdx = findRuleIndex(rules, rule);
  let updatedRules;

  if (ruleIdx === -1) {
    updatedRules = update(rules, { $push: [rule] });
  } else {
    updatedRules = update(rules, { $splice: [[ruleIdx, 1]] });
  }

  yield put(actions.setRules(updatedRules));
}

function* updateRule(action) {
  const { rule, op } = action;

  const rules = yield select(rulesSelector);
  const ruleIdx = findRuleIndex(rules, rule);
  const updatedRule = { ...rule, op };

  if (ruleIdx > -1) {
    const newRules = update(rules, { $splice: [[ruleIdx, 1, updatedRule]] });
    yield put(actions.setRules(newRules));
  }
}

function* removeRule(action) {
  const { rule } = action;
  const rules = yield select(rulesSelector);
  const ruleIdx = findRuleIndex(rules, rule);

  if (ruleIdx > -1) {
    const newRules = update(rules, { $splice: [[ruleIdx, 1]] });
    yield put(actions.setRules(newRules));
  }
}

function* filterBuilderApply(action) {
  const viewContext = yield select(viewContextSelector);
  const appliedFilter = yield select(getFirstAppliedFilter);
  const newFilter = yield select(buildFilterSelector);

  const combinedFilter = update(appliedFilter.rule, {
    $push: [...newFilter.rule],
  });

  const allRules = getRulesFromFilter(combinedFilter, true);
  const filterToApply = getFilterFromRules(allRules, true);

  if (viewContext === ViewContexts.RT_TRENDS) {
    const { history } = action.params;
    // push to noc drawer saga
    yield put(
      addFiltersFromNocDrawer({
        history,
        filters: filterToApply,
        type: NOC_MODE_TRENDS,
      }),
    );
  } else {
    yield put(pushDrilldownFilter(newFilter.name, newFilter.rule));
    yield put(applyFilter(filterToApply));
  }
}

function* initCompare(action) {
  const appliedCompareFilters = yield select(compareOnTrendsFiltersSelector);
  const { compareType } = action;
  // edit existing compare bar
  if (compareType === COMPARE_TYPES.trends && appliedCompareFilters.length > 0) {
    yield put(actions.editCompareFiltersOnTrends());
  } else {
    // init new compare bar
    yield put(
      actions.modifyCompareInit({
        ...defaultCompareState,
        type: compareType,
      }),
    );
    // do not init new filter, first action will create a new empty filter and add the rule
    // yield put(actions.initFilter());
  }
}

function* initCompareFilter({ params = null }: any) {
  const type = yield select(compareTypeSelector);
  const compareFilters = yield select(compareFiltersSelector);

  const limit = COMPARE_SERIES_LIMIT;
  if (compareFilters.length >= limit) {
    message.warning(getCompareLimitWarning(limit));
    return;
  }

  // init with empty for trends workflow
  let appliedFilter = yield select(getFirstAppliedFilter);
  if (type === COMPARE_TYPES.trends) {
    appliedFilter = ALL_TRAFFIC;
  }

  const rules = getRulesFromFilter(appliedFilter.rule, true);
  const filter = cloneDeep(appliedFilter);
  filter.meta = { cid: shortId(10) }; // temp identifier for use as key in render arr

  yield put(actions.setRules(Object.values(rules)));
  yield put(actions.addFilter(filter));
  yield put(actions.setActiveFilter(compareFilters.length)); // active index is length - 1

  if (params) {
    yield put(actions.toggleCompareRule(params.dimension, params.row));
  }
}

// update active id when a filter is removed
// user cannot remove an active filter
function* removeCompareFilter(action) {
  const { idx } = action;
  const activeFilterIdx = yield select(activeFilterIndexSelector);
  if (activeFilterIdx && activeFilterIdx > idx) {
    yield put(actions.setActiveFilter(activeFilterIdx - 1));
  }
}

function* setActiveFilter() {
  const activeFilter = yield select(activeFilterSelector);
  if (activeFilter) {
    const rules = getRulesFromFilter(activeFilter.rule, true);
    yield put(actions.setRules(Object.values(rules)));
  }
}

function* toggleCompareRule(action) {
  yield toggleRule(action);
  const updatedFilter = yield select(buildFilterSelector);
  yield updateActiveFilter(updatedFilter);
}

function* removeCompareRule(action) {
  yield removeRule(action);
  const updatedFilter = yield select(buildFilterSelector);
  yield updateActiveFilter(updatedFilter);
}

function* updateCompareRule(action) {
  yield updateRule(action);
  const updatedFilter = yield select(buildFilterSelector);
  yield updateActiveFilter(updatedFilter);
}

function* updateActiveFilter(updatedFilter) {
  const activeFilter = yield select(activeFilterSelector);
  const activeFilterIdx = yield select(activeFilterIndexSelector);

  if (activeFilter) {
    const cid = get(activeFilter, ['meta', 'cid'], shortId(10));
    yield put(actions.updateActiveFilter({ ...updatedFilter, meta: { cid } }));
  }

  // remove active filter if there are no rules left
  if (updatedFilter.rule.length === 0) {
    yield put(actions.removeFilter(updatedFilter, activeFilterIdx));
    yield put(actions.setActiveFilter(null));
  }
}

function* addTopFilters(action) {
  const { dimension, rows } = action;
  const filters = yield select(compareFiltersSelector);

  let appliedFilter = yield select(getFirstAppliedFilter);
  const type = yield select(compareTypeSelector);
  // init with empty for trends workflow
  if (type === COMPARE_TYPES.trends) {
    appliedFilter = ALL_TRAFFIC;
  }

  const rules = getRulesFromFilter(appliedFilter.rule, true);
  const limit = COMPARE_SERIES_LIMIT;

  const parsedFilters: any = [...filters];
  for (let i = 0; i < Math.min(rows.length, limit - filters.length); i++) {
    const row = rows[i];
    const rule = yield buildRule(dimension, row);
    const filter = getFilterFromRules({ ...rules, [getRuleId(rule)]: rule }, true);
    filter.meta = { cid: shortId(10) };
    parsedFilters.push(filter);
  }

  yield put(actions.setFilters(parsedFilters));
  yield put(actions.setActiveFilter(parsedFilters.length - 1)); // TODO: -1 case?
}

// compatible with current compare dashboard
function* compareFilters(action) {
  const viewContext = yield select(viewContextSelector);
  const { filters, modifyCompareId } = yield select(compareSelector);

  if (viewContext === ViewContexts.RT_FILTERS) {
    const { history } = action.params;
    // push to noc drawer saga
    yield put(
      addFiltersFromNocDrawer({
        history,
        filters: filters,
        type: NOC_MODE_FILTERS,
      }),
    );
  } else {
    // compare workflow
    const defaultZoomInterval = { start: 0, end: 100 };
    const compareConfig = yield select(compareConfigSelector);

    const ruleList = filters.map((filter) => {
      return { filters: getRulesFromFilter(filter.rule, true) };
    });

    const updatedConfig = {
      ...compareConfig,
      zoomInterval: defaultZoomInterval,
      filterBars: ruleList,
      savedFilters: [], // TODO: handle saved
    };

    yield put(
      triggerChangeTabs(
        addScrollingWindow({
          unsaved: true,
          dashboardTypeId: DASHBOARD_TYPE.COMPARE,
          config: updatedConfig,
        }),
        modifyCompareId,
      ),
    );
  }
}

function* compareFiltersOnTrends() {
  const { filters } = yield select(compareSelector);
  yield put(actions.applyCompareFiltersOnTrends(filters));
}

function* editCompareFiltersOnTrends() {
  const compareFilters = yield select(compareOnTrendsFiltersSelector);
  yield put(
    actions.modifyCompareInit({
      filters: compareFilters,
      activeFilterIndex: 0,
      modifyCompareId: null,
      type: COMPARE_TYPES.trends,
    }),
  );
  yield put(actions.setActiveFilter(0));
}

function* quickCompareFilterOnTrends(action) {
  const { dimension, row, options = {} } = action;
  const appliedCompareFilters = yield select(compareOnTrendsFiltersSelector);
  const quickCompareRuleSet = yield select(quickCompareRuleSetSelector);

  const rule = yield buildRule(dimension, row);

  // check if the new rule is part of any single rule filter
  // if the filter has only this one rule
  // then treat this as a toggle action
  try {
    const rid = getRuleId(rule);
    if (rid in quickCompareRuleSet) {
      // in a scenario like add top filters, we should not toggle out existing filters
      if (options.skipToggle) {
        return;
      }
      // remove filter
      const filterIndex = quickCompareRuleSet[rid];
      yield put(actions.removeCompareFiltersOnTrends(filterIndex));
      return;
    }
  } catch (err) {
    console.error('Rule toggle exception');
  }

  // validate filter limit
  if (appliedCompareFilters.length >= COMPARE_SERIES_LIMIT) {
    message.warning(getCompareLimitWarning(COMPARE_SERIES_LIMIT));
    return;
  }

  // apply new filter
  const filter = getFilterFromRules({ [getRuleId(rule)]: rule }, true);
  filter.meta = { cid: shortId(10) };
  yield put(actions.addCompareFilterOnTrends(filter));

  return true;
}

function* quickCompareTopFilters(action) {
  const { dimension, rows } = action;

  // iterate through top rows
  for (const row of rows) {
    const filters = yield select(compareOnTrendsFiltersSelector);
    // keep adding if within limit
    // if a row is alreay applied then it's skipped
    if (filters.length < COMPARE_SERIES_LIMIT) {
      yield quickCompareFilterOnTrends({ dimension, row, options: { skipToggle: true } });
    } else {
      break;
    }
  }
}

// compatible with current compare dashboard
function* modifyCompare(action) {
  const { config, activeEditId } = action;
  const { filterBars, savedFilters } = config;

  const parsedFilters = filterBars.map((bar) => getFilterFromRules(bar.filters, true));
  // retain name of saved filters until edited
  const parsedSavedFilters = savedFilters.map((bar) => ({
    ...getFilterFromRules(bar.filters, true),
    name: bar.name,
  }));

  yield put(
    actions.modifyCompareInit({
      filters: parsedFilters.concat(parsedSavedFilters),
      activeFilterIndex: 0,
      modifyCompareId: activeEditId,
      type: COMPARE_TYPES.dashboard,
    }),
  );
  // select and load rule set
  yield put(actions.setActiveFilter(0));
}

// redirect to trends
function* resetCompare() {
  const { pathname } = window.location;
  const homePage = isAppModule ? 'app-experience' : 'trends';

  if (pathname.includes('modify-comparison') || pathname.includes('modify-real-time')) {
    yield put(replace(`${baseUrl}/dashboard/${homePage}`));
  }

  yield put(actions.setViewContext(''));
}

function* resetFilter() {
  const { pathname } = window.location;
  if (pathname.includes('modify-real-time')) {
    if (isAppModule) {
      yield put(replace(`${baseUrl}/dashboard/app-experience`));
    } else {
      yield put(replace(`${baseUrl}/dashboard/trends`));
    }
  }
  yield put(actions.setViewContext(''));
}

function* watchInitRule() {
  yield takeLatest([types.INIT_RULE], initRule);
}

function* watchToggleRule() {
  yield takeLatest([types.TOGGLE_RULE], toggleRule);
}

function* watchUpdateRule() {
  yield takeLatest([types.UPDATE_RULE], updateRule);
}

function* watchRemoveRule() {
  yield takeLatest([types.REMOVE_RULE], removeRule);
}

function* watchApplyFilter() {
  yield takeLatest([types.APPLY_FILTER], filterBuilderApply);
}

function* watchResetFilter() {
  yield takeLatest([types.RESET_FILTER], resetFilter);
}

function* watchInitCompare() {
  yield takeLatest([types.INIT_COMPARE], initCompare);
}

function* watchInitCompareFilter() {
  yield takeLatest([types.INIT_COMPARE_FILTER], initCompareFilter);
}

function* watchToggleCompareRule() {
  yield takeLatest([types.TOGGLE_COMPARE_RULE], toggleCompareRule);
}

function* watchRemoveCompareRule() {
  yield takeLatest([types.REMOVE_COMPARE_RULE], removeCompareRule);
}

function* watchUpdateCompareRule() {
  yield takeLatest([types.UPDATE_COMPARE_RULE], updateCompareRule);
}

function* watchRemoveCompareFilter() {
  yield takeLatest([types.REMOVE_COMPARE_FILTER], removeCompareFilter);
}

function* watchSetActiveFilter() {
  yield takeLatest([types.SET_ACTIVE_FILTER], setActiveFilter);
}

function* watchCompareFilters() {
  yield takeLatest([types.COMPARE_FILTERS], compareFilters);
}

function* watchCompareFiltersOnTrends() {
  yield takeLatest([types.COMPARE_FILTERS_ON_TRENDS], compareFiltersOnTrends);
}

function* watchQuickCompareFilterOnTrends() {
  yield takeLatest([types.QUICK_COMPARE_FILTER], quickCompareFilterOnTrends);
}

function* watchQuickCompareTopFilters() {
  yield takeLatest([types.QUICK_COMPARE_TOP_FILTERS], quickCompareTopFilters);
}

function* watchEditCompareFiltersOnTrends() {
  yield takeLatest([types.EDIT_COMPARE_FILTERS_ON_TRENDS], editCompareFiltersOnTrends);
}

function* watchModifyCompare() {
  yield takeLatest([MODIFY_COMPARISON], modifyCompare);
}

function* watchResetCompare() {
  yield takeLatest([types.RESET_COMPARE], resetCompare);
}

function* watchAddTopFilters() {
  yield takeLatest([types.ADD_TOP_COMPARE_FILTERS], addTopFilters);
}

export default [
  watchInitRule,
  watchToggleRule,
  watchUpdateRule,
  watchRemoveRule,
  watchApplyFilter,
  watchResetFilter,
  watchInitCompare,
  watchInitCompareFilter,
  watchRemoveCompareFilter,
  watchToggleCompareRule,
  watchSetActiveFilter,
  watchRemoveCompareRule,
  watchUpdateCompareRule,
  watchCompareFilters,
  watchCompareFiltersOnTrends,
  watchQuickCompareFilterOnTrends,
  watchEditCompareFiltersOnTrends,
  watchQuickCompareTopFilters,
  watchModifyCompare,
  watchResetCompare,
  watchAddTopFilters,
];
